import { PrismicRichText } from '@prismicio/react';
import IconArrowRight from '@remote-com/norma/icons/arrow-right.svg';
import Image from 'next/image';
import { useState } from 'react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fieldHasContent, transformProperties } from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { AnimatedSection, sharedTransition } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';

import { OverviewSectionHeader } from '@/src/components/OverviewSection';
import {
  OverviewSectionSingleRow,
  ImageWrapper,
} from '@/src/components/OverviewSection/OverviewSectionRow';
import { tabletBreakpoint } from '@/src/domains/shared/breakpoints';
import { components } from '@/src/views/InternationalGlobalPayroll/slices//GlobalPayrollSlice';

const Wrapper = styled.section`
  ${fluidProp(['padding-top', 'padding-bottom'], '100px', '150px')}
  background-image: var(--colors-zircon);

  ${OverviewSectionHeader} {
    text-align: left;
    padding-bottom: 30px;
  }
`;

const OverviewSectionWrapper = styled.div`
  ${fluidProp(['padding-top', 'padding-bottom'], '100px', '200px')}

  ${ImageWrapper} {
    margin-bottom: 0;
  }
`;

export const Label = styled.div`
  ${({ theme }) => theme.typography.subHead}
  text-transform: uppercase;
  color: var(--colors-irisBlue);
  padding-bottom: 18px;
`;

export const Title = styled.div`
  ${({ theme }) => theme.typography.scale4}
  color: var(--colors-darkBlue);

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const Description = styled.div`
  ${({ theme }) => theme.typography.bodyLead}
  color: var(--colors-lynch);
`;

export const BenefitWrapper = styled.div`
  display: flex;

  ${tabletBreakpoint(css`
    flex-direction: column;
  `)}
`;

export const BenefitTitle = styled.div`
  ${tabletBreakpoint(css`
    margin-top: 0;

    > * {
      margin-top: 0;
    }
  `)}
`;

export const BenefitDescription = styled.div`
  color: #778ca3;
  margin-top: 0;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const BenefitContent = styled.div`
  ${({ $isVisible }) => ($isVisible ? `display: block; ` : `display: none;`)};
  margin-left: 60px;

  ${tabletBreakpoint(css`
    margin-left: 0;
  `)}
`;

export const BenefitHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 17px;

  svg {
    width: 18px;
    margin-left: 8px;

    ${tabletBreakpoint(css`
      margin-left: 0;
      transform: rotate(0.25turn);
    `)}
  }

  ${tabletBreakpoint(css`
    flex-direction: column;
    margin: auto;
  `)}
`;

export const BenefitsList = styled.div`
  min-width: 30%;

  ${tabletBreakpoint(css`
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-bottom: 20px;
  `)}
`;

export const BenefitsIcon = styled.div`
  min-width: 40px;
`;

export const BenefitsEntry = styled.button`
  display: flex;
  padding-left: 21px;
  width: 246px;
  height: 62px;
  background-color: transparent;
  border-radius: 20px;
  margin-bottom: 5px;
  border: none;
  opacity: 1;
  ${sharedTransition('opacity, background-color')}

  ${({ $active }) => {
    switch ($active) {
      case true: {
        return css`
          background-color: var(--colors-blank);
        `;
      }
      case false:
      default: {
        return css`
          svg {
            opacity: 0;
          }
        `;
      }
    }
  }}

  ${tabletBreakpoint(css`
    width: 30%;
    max-width: 100px;
    min-height: 200px;
    padding-left: 0;
    justify-content: center;
  `)}
`;

export function BenefitsContractorsSlice({ slice }) {
  const { primary } = transformProperties(slice);
  const {
    sectionLabel,
    sectionTitle,
    sectionDescription,
    benefitsLabel,
    benefitsTitle,
    benefitsDescription,
    illustration,
  } = primary;
  const { fields: overviewSections } = slice;

  const [activeBenefitIndex, setActiveBenefitIndex] = useState(0);
  const hasHeading =
    fieldHasContent(benefitsLabel) ||
    fieldHasContent(benefitsTitle) ||
    fieldHasContent(benefitsDescription);

  return (
    <Wrapper>
      <OverviewSectionWrapper>
        <OverviewSectionSingleRow
          label={sectionLabel}
          title={sectionTitle}
          description={sectionDescription}
          illustration={illustration}
          illustration_on_the_edge
        />
      </OverviewSectionWrapper>
      {hasHeading && (
        <AnimatedSection ComponentName={OverviewSectionHeader}>
          <ContentContainer>
            {fieldHasContent(benefitsLabel) && <Label>{benefitsLabel}</Label>}
            {fieldHasContent(benefitsTitle) && (
              <Title>
                <PrismicRichText field={benefitsTitle} />
              </Title>
            )}
            {fieldHasContent(benefitsDescription) && (
              <Description>
                <PrismicRichText field={benefitsDescription} components={components} />
              </Description>
            )}
          </ContentContainer>
        </AnimatedSection>
      )}
      <ContentContainer>
        <BenefitWrapper>
          <BenefitsList>
            {overviewSections.map(({ icon, title }, index) => (
              <BenefitsEntry
                key={index}
                onClick={() => setActiveBenefitIndex(index)}
                $active={activeBenefitIndex === index}
              >
                <BenefitHeader>
                  {illustration && (
                    <BenefitsIcon>
                      <div>
                        <Image
                          src={icon?.url}
                          alt={icon?.alt || ''}
                          width={icon?.dimensions.width}
                          height={icon?.dimensions.height}
                          quality="100"
                        />
                      </div>
                    </BenefitsIcon>
                  )}
                  {fieldHasContent(title) && (
                    <BenefitTitle>
                      <PrismicRichText field={title} />
                    </BenefitTitle>
                  )}
                  <IconArrowRight />
                </BenefitHeader>
              </BenefitsEntry>
            ))}
          </BenefitsList>
          <div>
            {overviewSections.map(({ icon, title, description }, index) => (
              <BenefitContent key={index} $isVisible={activeBenefitIndex === index}>
                <BenefitHeader>
                  {illustration && (
                    <BenefitsIcon>
                      <ImageWrapper>
                        <Image
                          src={icon?.url}
                          alt={icon?.alt || ''}
                          width={icon?.dimensions.width}
                          height={icon?.dimensions.height}
                          quality="100"
                        />
                      </ImageWrapper>
                    </BenefitsIcon>
                  )}

                  {fieldHasContent(title) && (
                    <BenefitTitle>
                      <PrismicRichText field={title} />
                    </BenefitTitle>
                  )}
                </BenefitHeader>

                {fieldHasContent(description) && (
                  <BenefitDescription>
                    <PrismicRichText field={description} components={components} />
                  </BenefitDescription>
                )}
              </BenefitContent>
            ))}
          </div>
        </BenefitWrapper>
      </ContentContainer>
    </Wrapper>
  );
}
